import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Cpu, FlaskConical, ShieldBan, Trash2, Plus, BrainCircuit } from "lucide-react";
import { toast } from "sonner";

const MODE_STYLES = {
  allow: "bg-[#dcfce7] text-[#15803d]",
  deny: "bg-[#fee2e2] text-[#b91c1c]",
  warn: "bg-[#fef3c7] text-[#92400e]",
};

const SERVICE_COLORS = ["#00579e", "#0ea5e9", "#6d28d9", "#15803d", "#b45309", "#64748b"];

export default function AICostSuitePanel() {
  const [spend, setSpend] = useState(null);
  const [idle, setIdle] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [sim, setSim] = useState(null);
  const [simInputs, setSimInputs] = useState({
    model: "gemini-2.5-flash", avg_qpm: 30, avg_input_tokens: 1200,
    avg_output_tokens: 400, peak_to_avg_ratio: 2,
  });
  const [newPolicy, setNewPolicy] = useState({ model_pattern: "", mode: "warn", scope: "all", note: "" });
  const [simulating, setSimulating] = useState(false);

  const load = () => {
    api.aiCost.spend(30).then(setSpend).catch(console.error);
    api.aiCost.idle().then(setIdle).catch(console.error);
    api.aiCost.policies().then(setPolicies).catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const runSim = async () => {
    setSimulating(true);
    try {
      setSim(await api.aiCost.simulate({
        ...simInputs,
        avg_qpm: parseFloat(simInputs.avg_qpm) || 0,
        avg_input_tokens: parseInt(simInputs.avg_input_tokens) || 0,
        avg_output_tokens: parseInt(simInputs.avg_output_tokens) || 0,
        peak_to_avg_ratio: parseFloat(simInputs.peak_to_avg_ratio) || 1,
      }));
    } catch (e) {
      toast.error("Simulation failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSimulating(false);
    }
  };

  const addPolicy = async () => {
    if (!newPolicy.model_pattern.trim()) {
      toast.error("Model pattern is required");
      return;
    }
    try {
      await api.aiCost.addPolicy(newPolicy);
      toast.success("Policy added");
      setNewPolicy({ model_pattern: "", mode: "warn", scope: "all", note: "" });
      api.aiCost.policies().then(setPolicies);
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const deletePolicy = async (id) => {
    try {
      await api.aiCost.deletePolicy(id);
      toast.success("Policy removed");
      api.aiCost.policies().then(setPolicies);
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-cost-suite-panel">
      {/* Spend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cs-card p-5" data-testid="ai-spend-chart">
          <div className="flex items-center gap-2 mb-1">
            <BrainCircuit className="w-4 h-4 text-[#00579e]" />
            <h3 className="cs-heading text-sm font-semibold">GCP AI Spend — last {spend?.window_days ?? 30} days</h3>
            <span className={`cs-pill cs-mono text-[10px] ml-auto ${spend?.data_source === "live" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fef3c7] text-[#92400e]"}`}>
              {spend?.data_source === "live" ? "LIVE" : "REFERENCE"}
            </span>
          </div>
          <div className="cs-kpi-value text-2xl text-[#00579e] mb-3" data-testid="ai-spend-total">{fmtUSD(spend?.total_usd)}</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={spend?.by_service || []} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" tickFormatter={(v) => fmtUSD(v, { compact: true })} fontSize={10} />
              <YAxis type="category" dataKey="service" width={150} fontSize={10} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="cost_usd" radius={[0, 3, 3, 0]}>
                {(spend?.by_service || []).map((_, i) => (
                  <Cell key={i} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="cs-card p-5 overflow-hidden" data-testid="ai-sku-table">
          <h3 className="cs-heading text-sm font-semibold mb-3">Top AI SKUs</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">SKU</th>
                <th className="py-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {(spend?.by_sku || []).map((s, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-2">
                    <div className="truncate max-w-[280px]" title={s.sku}>{s.sku}</div>
                    <div className="text-[10px] text-muted-foreground">{s.service}</div>
                  </td>
                  <td className="py-1.5 text-right cs-mono font-semibold">{fmtUSD(s.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Idle AI capacity */}
      <div className="cs-card p-5" data-testid="ai-idle-panel">
        <div className="flex items-center gap-2 mb-3">
          <Cpu className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Idle AI Capacity</h3>
          <span className="ml-auto text-xs text-muted-foreground">
            {idle?.count ?? 0} findings · <span className="cs-mono font-semibold text-[#b91c1c]">{fmtUSD(idle?.total_monthly_waste_usd)}</span>/mo wasted
          </span>
        </div>
        {(idle?.items || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center" data-testid="no-idle-ai">No idle GPU or Vertex endpoint findings in the latest scan.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Finding</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Resource</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Project</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground text-right">Waste / mo</th>
              </tr>
            </thead>
            <tbody>
              {(idle?.items || []).map((it) => (
                <tr key={it.opportunity_id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-medium">{it.type}</td>
                  <td className="px-3 py-2 cs-mono text-muted-foreground truncate max-w-[180px]" title={it.resource_name}>{it.resource_name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.project_id}</td>
                  <td className="px-3 py-2 text-right cs-mono font-semibold text-[#b91c1c]">{fmtUSD(it.monthly_waste_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* PTU simulator */}
      <div className="cs-card p-5" data-testid="ptu-simulator">
        <div className="flex items-center gap-2 mb-1">
          <FlaskConical className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">What-If: PAYG vs Provisioned Throughput (Vertex AI)</h3>
        </div>
        <p className="text-[11px] text-muted-foreground mb-4">Model your workload to see whether committed GSUs beat on-demand token pricing.</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Model</label>
            <Select value={simInputs.model} onValueChange={(v) => setSimInputs({ ...simInputs, model: v })}>
              <SelectTrigger className="rounded-sm h-9 text-xs mt-1" data-testid="sim-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                <SelectItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash-Lite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {[
            ["avg_qpm", "Avg queries / min"],
            ["avg_input_tokens", "Avg input tokens"],
            ["avg_output_tokens", "Avg output tokens"],
            ["peak_to_avg_ratio", "Peak / avg ratio"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{label}</label>
              <Input
                type="number"
                value={simInputs[k]}
                onChange={(e) => setSimInputs({ ...simInputs, [k]: e.target.value })}
                data-testid={`sim-input-${k}`}
                className="rounded-sm h-9 text-xs mt-1 cs-mono"
              />
            </div>
          ))}
        </div>
        <Button onClick={runSim} disabled={simulating} data-testid="run-simulation" className="mt-4 rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5">
          {simulating ? "Simulating…" : "Run Simulation"}
        </Button>

        {sim && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sim-results">
            <div className={`border p-4 rounded-sm ${sim.cheaper_option === "payg" ? "border-[#15803d] bg-[#f0fdf4]" : "border-border"}`}>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Pay-as-you-go</div>
              <div className="cs-kpi-value text-xl mt-1" data-testid="sim-payg-cost">{fmtUSD(sim.payg_monthly_usd)}<span className="text-xs text-muted-foreground">/mo</span></div>
              {sim.cheaper_option === "payg" && <div className="text-[10px] text-[#15803d] font-semibold mt-1 uppercase">✓ cheaper option</div>}
            </div>
            <div className={`border p-4 rounded-sm ${sim.cheaper_option === "provisioned_throughput" ? "border-[#15803d] bg-[#f0fdf4]" : "border-border"}`}>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Provisioned ({sim.provisioned?.gsus_needed} GSU)</div>
              <div className="cs-kpi-value text-xl mt-1" data-testid="sim-pt-cost">{fmtUSD(sim.provisioned?.monthly_usd)}<span className="text-xs text-muted-foreground">/mo</span></div>
              <div className="text-[10px] text-muted-foreground mt-1">{sim.provisioned?.avg_utilization_pct}% avg utilization</div>
            </div>
            <div className="border border-border p-4 rounded-sm bg-[hsl(var(--cs-tint))]">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Recommendation</div>
              <p className="text-xs text-foreground mt-1 leading-relaxed" data-testid="sim-recommendation">{sim.recommendation}</p>
            </div>
          </div>
        )}
      </div>

      {/* Governance policies */}
      <div className="cs-card p-5" data-testid="ai-policies-panel">
        <div className="flex items-center gap-2 mb-1">
          <ShieldBan className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">AI Governance — Model Policies</h3>
        </div>
        <p className="text-[11px] text-muted-foreground mb-4">Allow / warn / deny model usage by pattern and scope. Policies feed procurement & platform guardrails.</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Model pattern</label>
            <Input value={newPolicy.model_pattern} placeholder="e.g., gemini-*-preview"
              onChange={(e) => setNewPolicy({ ...newPolicy, model_pattern: e.target.value })}
              data-testid="policy-pattern-input" className="rounded-sm h-9 text-xs mt-1 cs-mono" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Mode</label>
            <Select value={newPolicy.mode} onValueChange={(v) => setNewPolicy({ ...newPolicy, mode: v })}>
              <SelectTrigger className="rounded-sm h-9 text-xs mt-1" data-testid="policy-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">allow</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="deny">deny</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Scope</label>
            <Input value={newPolicy.scope} onChange={(e) => setNewPolicy({ ...newPolicy, scope: e.target.value })}
              data-testid="policy-scope-input" className="rounded-sm h-9 text-xs mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Note</label>
            <Input value={newPolicy.note} onChange={(e) => setNewPolicy({ ...newPolicy, note: e.target.value })}
              data-testid="policy-note-input" className="rounded-sm h-9 text-xs mt-1" />
          </div>
          <Button onClick={addPolicy} data-testid="add-policy-button" className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Policy
          </Button>
        </div>

        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Pattern</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Mode</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Scope</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Note</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0" data-testid={`policy-row-${p.id}`}>
                <td className="px-3 py-2 cs-mono font-medium">{p.model_pattern}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${MODE_STYLES[p.mode] || ""}`}>{p.mode}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{p.scope}</td>
                <td className="px-3 py-2 text-muted-foreground max-w-[280px] truncate" title={p.note}>{p.note}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deletePolicy(p.id)} data-testid={`delete-policy-${p.id}`}
                    className="text-muted-foreground hover:text-[#b91c1c] transition-colors duration-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
